<script setup lang="ts">
import { computed } from 'vue';

import type {
  GlobalSearchApplicationRow,
  GlobalSearchPartition,
} from '@/core/conversations/conversation-index/conversation-index.type';

import GlobalCandidateSearchRow from './GlobalCandidateSearchRow.vue';
import GlobalCandidateSearchGroup from './GlobalCandidateSearchGroup.vue';

const props = defineProps<{
  query: string;
  currentJobTitle: string;
  partition: GlobalSearchPartition;
  loading: boolean;
  error: boolean;
  /**
   * True when cross-job rows were hidden by the client-side row cap. Only a
   * boolean — the exact hidden count cannot be derived honestly, so it is
   * never displayed. The endpoint's own candidate cap is a separate signal
   * (matched vs loaded counts) because those hidden candidates were never
   * partitioned and may belong to either zone.
   */
  hasHiddenRows: boolean;
  /**
   * True when the backend matched more candidates than the 100 it returned.
   * The endpoint reports no exact total, so no message may ever claim one —
   * only "100+". Old backends without the flag degrade to false upstream.
   */
  hasMore: boolean;
  /** Candidates the backend actually returned (endpoint caps at 100). */
  loadedCandidateCount: number;
}>();

const emit = defineEmits<{
  (e: 'select', payload: GlobalSearchApplicationRow): void;
  (e: 'retry'): void;
}>();

const hasCurrentJobRows = computed(() => props.partition.currentJobRows.length > 0);
const hasOtherJobRows = computed(() => props.partition.otherJobGroups.length > 0);
const hasAnyRows = computed(() => hasCurrentJobRows.value || hasOtherJobRows.value);

/**
 * The endpoint matched more candidates than it returned. The hidden
 * candidates were never partitioned, so neither zone may make a definitive
 * "no matches" claim while this is true.
 */
const isApiCapped = computed(() => props.hasMore);

/**
 * The single account-wide empty state renders only after the request has
 * settled with the backend itself reporting zero matches — never while
 * loading, never on error, and never when a matched candidate was merely
 * filtered out client-side (pre-pipeline statuses have no stage to open).
 */
const isEmptyEverywhere = computed(
  () =>
    !props.loading &&
    !props.error &&
    !hasAnyRows.value &&
    props.loadedCandidateCount === 0 &&
    !isApiCapped.value,
);

/**
 * The backend matched candidates, every loaded application was filtered out
 * client-side (NOTIFIED / NOT_INTERESTED have no stage to open), and nothing
 * was hidden by the endpoint cap — so "no candidates anywhere" would be false.
 */
const isMatchedButNotSelectable = computed(
  () =>
    !props.loading &&
    !props.error &&
    !hasAnyRows.value &&
    props.loadedCandidateCount > 0 &&
    !isApiCapped.value,
);

const otherJobsCountLabel = computed(() => {
  const shown = props.partition.otherJobsShownCount;
  // The API cap hides candidates that could belong to either zone, so zone B
  // may not claim an exact count under it either.
  return props.hasHiddenRows || isApiCapped.value ? `${shown}+` : `${shown}`;
});

/** Zone empty-state suffix once the endpoint cap makes counts non-exhaustive. */
const zoneEmptyQualifier = computed(() => (isApiCapped.value ? ' among the candidates shown' : ''));
</script>

<template>
  <div class="flex w-full flex-col" data-test="global-search-results">
    <!-- Recoverable error: the resting list is untouched behind this state.
         While a changed query is pending, the skeleton takes precedence so a
         stale error does not flash for the whole debounce window. -->
    <div
      v-if="error && !loading"
      class="flex flex-col items-center gap-2 px-4 py-10 text-center"
      data-test="global-search-error"
    >
      <p class="text-sm font-bold text-shade-900">Search isn't available right now</p>
      <p class="text-xs text-shade-800">Check your connection, then try again.</p>
      <button
        type="button"
        class="mt-1 rounded-md bg-shade-900 px-3 py-1.5 text-xs font-semibold text-white"
        data-test="global-search-retry"
        @click="emit('retry')"
      >
        Try again
      </button>
    </div>

    <!-- One account-wide empty state, only when the backend matched nothing. -->
    <div
      v-else-if="isEmptyEverywhere"
      class="flex flex-col items-center gap-1 px-4 py-10 text-center"
      data-test="global-search-empty-all"
    >
      <p class="text-sm font-bold text-shade-900">
        No candidates matching &ldquo;{{ query }}&rdquo; anywhere in your account
      </p>
      <p class="text-xs text-shade-800">Search looks at name, email, and phone number.</p>
    </div>

    <!-- The backend matched candidates, but none have an application in a
         stage this search can open (e.g. sourced or not interested). -->
    <div
      v-else-if="isMatchedButNotSelectable"
      class="flex flex-col items-center gap-1 px-4 py-10 text-center"
      data-test="global-search-empty-filtered"
    >
      <p class="text-sm font-bold text-shade-900">
        No open applications match &ldquo;{{ query }}&rdquo;
      </p>
      <p class="text-xs text-shade-800">
        A matching candidate exists, but none of their applications are in a stage you can open
        here. Try the Candidate Archive.
      </p>
    </div>

    <template v-else>
      <!-- Endpoint-cap disclosure: hidden candidates were never partitioned,
           so the uncertainty is represented globally, above both zones. The
           backend reports no exact total, so the copy never claims one. -->
      <p
        v-if="isApiCapped && !loading"
        class="px-4 pb-1 pt-2 text-xs text-shade-700"
        data-test="global-search-cap-notice"
      >
        100+ matches; keep typing
      </p>

      <!-- Zone A: the selected job, across every status/stage -->
      <div
        class="flex items-baseline justify-between px-4 pb-2 pt-4"
        data-test="global-search-zone-current-job"
      >
        <span class="text-[11px] font-semibold uppercase tracking-[0.08em] text-shade-800">
          In <span class="normal-case text-shade-880">{{ currentJobTitle }}</span>
        </span>
        <span
          v-if="!loading"
          class="text-xs tabular-nums text-shade-700"
          data-test="global-search-current-job-count"
        >
          {{ partition.currentJobRows.length }}{{ isApiCapped ? '+' : '' }}
        </span>
      </div>

      <template v-if="loading">
        <div class="px-4" data-test="global-search-skeleton">
          <div v-for="index in 2" :key="index" class="animate-pulse py-2">
            <div class="h-3 w-3/5 rounded bg-tint-60" />
            <div class="mt-1.5 h-3 w-2/5 rounded bg-tint-60" />
          </div>
        </div>
      </template>
      <template v-else-if="hasCurrentJobRows">
        <!-- Hairline divider between candidates: with 8px row padding either
             side it makes between-candidate spacing (~17px) looser than the
             within-group spacing of a multi-application candidate (~14px). -->
        <div class="divide-y divide-tint-40">
          <GlobalCandidateSearchRow
            v-for="row in partition.currentJobRows"
            :key="row.application.applicationId"
            :row="row"
            :query="query"
            show-candidate-name
            @select="emit('select', $event)"
          />
        </div>
      </template>
      <p v-else class="px-4 py-2 text-xs text-shade-700" data-test="global-search-zone-a-empty">
        No matches in {{ currentJobTitle }}{{ zoneEmptyQualifier }}
      </p>

      <!-- Zone B: every other job in the account -->
      <div
        class="flex items-baseline justify-between px-4 pb-2 pt-6"
        data-test="global-search-zone-all-jobs"
      >
        <span class="text-[11px] font-semibold uppercase tracking-[0.08em] text-shade-800">
          Across all jobs
        </span>
        <span
          v-if="!loading"
          class="text-xs tabular-nums text-shade-700"
          data-test="global-search-other-jobs-count"
        >
          {{ otherJobsCountLabel }}
        </span>
      </div>

      <template v-if="loading">
        <p class="px-4 py-1 text-xs text-shade-700">Searching all jobs&hellip;</p>
        <div class="px-4" data-test="global-search-skeleton-all-jobs">
          <div v-for="index in 3" :key="index" class="animate-pulse py-2">
            <div class="h-3 w-3/5 rounded bg-tint-60" />
            <div class="mt-1.5 h-3 w-2/5 rounded bg-tint-60" />
          </div>
        </div>
      </template>
      <template v-else-if="hasOtherJobRows">
        <div class="divide-y divide-tint-40">
          <GlobalCandidateSearchGroup
            v-for="group in partition.otherJobGroups"
            :key="group.conversationIndex.conversationId"
            :group="group"
            :query="query"
            @select="emit('select', $event)"
          />
        </div>
        <p
          v-if="hasHiddenRows"
          class="px-4 py-2 text-xs text-shade-700"
          data-test="global-search-overflow"
        >
          More matches than shown &mdash; keep typing to narrow.
        </p>
      </template>
      <p v-else class="px-4 py-2 text-xs text-shade-700" data-test="global-search-zone-b-empty">
        No matches in your other jobs{{ zoneEmptyQualifier }}
      </p>
    </template>
  </div>
</template>
